import {TitleBox} from "../general/TitleBox";
import {useAuth} from "../auth/AuthHook";
import {Empty, Input, Table} from "antd";
import {LockIcon, TeamsIcon} from "../components/Icons";
import React, {ReactPropTypes, useCallback, useEffect, useState} from "react";
import {useSupportCase} from "../support/CaseHook";
import {ActionButton} from "../components/ActionButton";
import {useTranslation} from "react-i18next";
import {Tooltip} from "flowbite-react";
import '../filter/CaseFilter.css'
import {EditableBox} from "../components/EditableBox";

interface TenantAssignment {
    tenant: string;
    org_id: string | null;
}

interface TenantRowData extends TenantAssignment {
    target_org_id: string;
}

export function TeamsSettings() {
    const {isLoggedIn, isSysAdmin, userData} = useAuth();
    const {fetchData} = useSupportCase();
    const [tenants, setTenants] = useState<TenantRowData[]>([]);
    const [loading, setLoading] = useState(false);
    const {t} = useTranslation();

    const userOrganization = userData?.organization ?? "";

    useEffect(() => {
        const fetchTenants = async () => {
            if (!isLoggedIn()) {
                return;
            }
            setLoading(true);
            try {
                const res = await fetchData("integrations/teams/tenants", "GET");
                const fetchedTenants: TenantAssignment[] = await res.json();
                const tenantsWithTargetOrg = fetchedTenants.map((tenant) => ({
                    ...tenant,
                    target_org_id: userOrganization,
                }));
                setTenants(tenantsWithTargetOrg);
            } catch (e) {
                // Handled already
            } finally {
                setLoading(false);
            }
        };
        fetchTenants();
    }, [isLoggedIn, fetchData, userOrganization]);

    const handleTargetOrgChange = useCallback((tenantId: string, newTargetOrg: string) => {
        setTenants((prev) =>
            prev.map((t) =>
                t.tenant === tenantId ? {...t, target_org_id: newTargetOrg} : t
            )
        );
    }, []);

    const handleAssign = useCallback(async (tenantId: string, targetOrgId: string) => {
        await fetchData(`integrations/teams/tenants/${tenantId}/assign`, "POST", {
            org_id: targetOrgId,
        });
        setTenants((prev) =>
            prev.map((t) =>
                t.tenant === tenantId ? {...t, org_id: targetOrgId} : t
            )
        );
    }, [fetchData]);

    const handleDelete = useCallback(async (tenantId: string) => {
        await fetchData(`integrations/teams/tenants/${tenantId}`, "DELETE");
        setTenants((prev) => prev.filter((t) => t.tenant !== tenantId));
    }, [fetchData]);

    const isPossibleToAssign = isSysAdmin;

    return (
        <div className="w-full lg:px-16 xl:px-32">
            <TitleBox lineHidden={true}>
                <h1 className="flex text-2xl font-bold py-8 items-center justify-center gap-2">
                    <TeamsIcon />
                    {t("Teams Integration Settings")}
                </h1>
            </TitleBox>
            {!isLoggedIn() && (
                <Empty
                    description={
                        <span className="text-gray-300 text-base">
                            {t("You need to log in to see Teams integrations.")}
                        </span>
                    }
                    image={<LockIcon />}
                />
            )}
            {isLoggedIn() && (
                <div className={"mb-6 flex flex-col"}>
                    <Table<TenantRowData>
                        dataSource={tenants}
                        loading={loading}
                        columns={[
                            {
                                title: t("Tenant"),
                                dataIndex: "tenant",
                                key: "tenant",
                            },
                            {
                                title: t("Current Organization"),
                                dataIndex: "org_id",
                                key: "org_id",
                                render: (org_id: string | null) => org_id ?? t("(Not assigned)"),
                            },
                            {
                                title: t("Target Organization"),
                                dataIndex: "target_org_id",
                                key: "target_org_id",
                                render: (_: string, record: TenantRowData) => (
                                    <EditableBox initialValue={record.target_org_id} onAccept={(updatedText) => {handleTargetOrgChange(record.tenant, updatedText)}} onCancel={() => {}}/>
                                ),
                            },
                            {
                                title: t("Actions"),
                                key: "action",
                                render: (_: unknown, record: TenantRowData) => (
                                    <div className="flex flex-row gap-2">
                                        <Tooltip
                                        content={
                                            isPossibleToAssign ? t("Click to assign organization") : t("Not a system administrator")
                                        }
                                        >
                                            <ActionButton
                                                onClick={() => handleAssign(record.tenant, record.target_org_id)}
                                                disabled={!isPossibleToAssign || record.target_org_id === ""}
                                                className="w-auto"
                                            >
                                                {t("Assign")}
                                            </ActionButton>

                                        </Tooltip>
                                        <Tooltip
                                            content={
                                                isPossibleToAssign ? t("Click to delete integration") : t("Not a system administrator")
                                            }
                                        >
                                            <ActionButton
                                                onClick={() => handleDelete(record.tenant)}
                                                disabled={!isPossibleToAssign}
                                                className="w-auto bg-red-600 hover:bg-red-700"
                                            >
                                                {t("Delete Teams Integration Action")}
                                            </ActionButton>
                                        </Tooltip>
                                    </div>

                            ),
                            },
                        ]}
                        rowKey="tenant"
                        pagination={false}
                        prefixCls={"arten-search"}
                        rowClassName={"case-filter-table-row"}
                        className={"case-filter-table"}
                        rootClassName={"case-filter-table-root"}
                        bordered={false}
                        sortDirections={["ascend", "descend"]}
                        showSorterTooltip={true}
                        components={
                            {
                                header: {
                                    wrapper: (props: ReactPropTypes) => (
                                        <thead {...props} className={"case-filter-table-header"}/>
                                    ),
                                },

                            }
                        }

                    />
                </div>
            )}
        </div>
    );
}

